// App.js
import React, { useState, useRef } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import MotionText from './components/MotionText';
import buildCytoscapeElements from './components/GraphVisualization';
import AboutDialog from './components/AboutDialog';
import ToolbarSection from './components/ToolbarSection';
import logo from './images/logo2.webp';

import { Turnstile } from '@marsidev/react-turnstile';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import { 
  LinearProgress, 
  Container, 
  Box, 
  Paper
} from '@mui/material'; // Add Material-UI components



const App = () => {
  const [csvData, setCsvData] = useState([]);
  const [initTree, setInitTree] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [trackTree, setTrackTree] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const cyRef = useRef(null); // https://www.w3schools.com/react/react_useref.asp
  const switchtoggle = useRef(false); // To track if handleNodeClick has been executed
  const [fileUploaded, setFileUploaded] = useState(false); // State to track file upload status
  const [aboutOpen, setAboutOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const currentLayout = useRef('circle');

  const [loading, setLoading] = useState(false);

  // Define a function for styling the Cytoscape elements
  const applyCytoscapeStyles = (cy) => {
    cy.style()
    .selector('edge')
    .style({
      'curve-style': 'straight',
      'target-arrow-shape': 'triangle',
      'target-arrow-color': '#000000',
      'width': .5,
      'label': 'data(label)',
      'min-zoomed-font-size': 10,
      'font-size': '10px',
      'font-family': '"Courier New", monospace', // Terminal-style font
      'font-weight': 'bold', // Bold font weight
      'text-rotation': 'autorotate'
    })
    .selector('node')
    .style({
      'font-size': '20px',
      'background-color': '#808080', // Gray color
      'color': '#000000',
      'font-family': '"Courier New", monospace', // Terminal-style font
      'font-weight': 'bold', // Bold font weight
      'padding': '10' // Add padding around the node
    })
    .selector('node:selected')
    .style({
      "border-width": "6px",
      "border-color": "red",
      "border-opacity": "0.5",
      "background-color": "#000000",
      "text-outline-color": "#000000"
    })
  .update();

  if(currentLayout.current === 'circle'){
    const layout = cy.layout(
      {
        name: 'circle',
        fit: true, // whether to fit the viewport to the graph
        padding: 30, // the padding on fit
        avoidOverlap: true, // prevents node overlap, may overflow boundingBox and radius if not enough space
        startAngle: 3 / 2 * Math.PI, // where nodes start in radians
        clockwise: true, // whether the layout should go clockwise (true) or counterclockwise/anticlockwise (false)
        animate: true // whether to transition the node positions
      }
    )
    layout.run();
  }else{
    const layout = cy.layout(
      {
        name: 'breadthfirst',
        //fit: true, // whether to fit the viewport to the graph
        directed: true, // whether the tree is directed downwards (or edges can point in any direction if false)
        padding: 30, // padding on fit
       
        grid: false, // whether to create an even grid into which the DAG is placed (circle:false only)
        spacingFactor: 1.75, // positive spacing factor, larger => more space between nodes (N.B. n/a if causes overlap)
        avoidOverlap: true, // prevents node overlap, may overflow boundingBox if not enough space
        nodeDimensionsIncludeLabels: false, // Excludes the label when calculating node bounding boxes for the layout algorithm
        animate: true, // whether to transition the node positions
      }
    )
    layout.run();
  }

  cy.center();

  };
  
  const handleCheckboxChange = (event) => {
    switchtoggle.current = event.target.checked;
  };
  
  const handleCsvUpload = (data) => {
    try {
      // Clear the existing CSV data and reset state
      setCsvData([]);
      setInitTree([]);
      setTrackTree([]);
      setSearchTerm('');
      setModalOpen(false);
      setFileUploaded(false); // Reset file upload status
      //switchtoggle.current = false; // Reset BFS toggle
      currentLayout.current = 'circle'; // Reset layout to default
  
      // Set the new CSV data
      setCsvData(data);
  
      // Open the modal
      setModalOpen(true);
  
    } catch (error) {
      console.error('Error during CSV upload:', error);
    }
  }

  const handleColumnsSelect = (columns) => {
    setLoading(true);
    setTimeout(() => {
      // Reset all relevant state variables
      setInitTree([]);
      setTrackTree([]);
      setModalOpen(false);
      //setIsFileUploaded(false);

      // Set the selected columns and build the graph elements
      const elements = buildCytoscapeElements(csvData, columns);
      setInitTree(elements);
      setTrackTree(elements);
      setModalOpen(false);
      // Set fileUploaded to true to hide MotionText and display the graph
      setFileUploaded(true);

      setLoading(false);
    }, 500);

  };

  // New function for handling search
  const handleDynamicSearch = (e) => {
    //TODO: There is a bug in
    const value = e.target.value;
   
    setSearchTerm(value); 
    if (cyRef.current) {
      currentLayout.current = 'breadthfirst';
      const cy = cyRef.current;
      cy.removeAllListeners();
      cy.elements().remove();
      cy.add(initTree);
      
      const matchingNodes = cy.nodes().filter(node =>
        node.data('label').toLowerCase().includes((value.toLowerCase()))
      );

      if (switchtoggle.current === false) {
  
        const neighborhood = matchingNodes.closedNeighborhood();

        // Clear the graph
        cy.elements().remove();
  
        // Add the clicked node and its edges
        cy.add(neighborhood);
  
        // Fit the graph to the neighborhood
        applyCytoscapeStyles(cy);
      }
      else{
        // Use Cytoscape's breadthFirstSearch to get reachable elements
        const startNodeId = matchingNodes.id();
        const bfsResult = cy.elements().breadthFirstSearch({
          roots: `#${startNodeId}`,
          directed: true
        });

        const reachableElements = bfsResult.path;

        // Clear the graph
        cy.elements().remove();

        // Add the reachable elements
        cy.add(reachableElements);

        // Apply the layout to the graph
        applyCytoscapeStyles(cy);
      }
      
    }
  };

  const handleNodeClick = (event) => {

    if (event === null) {
      resetGraphState();
      return;
    }

    setSearchTerm(''); // Clear the search text
    // get clicked node
    const clickedNode = event.target;
   
    if (cyRef.current) {
      currentLayout.current = 'breadthfirst';
      const cy = cyRef.current;
      
      cy.removeAllListeners();
      cy.on('click', 'node', handleNodeClick);
      // Reset the tree to its initial state
      cy.elements().remove();
      cy.add(initTree);
 
      const matchingNodes = cy.nodes().filter(node =>
        node.data('label') === clickedNode.data('label')
      );
      if(switchtoggle.current === false){

        const neighborhood = matchingNodes.closedNeighborhood();

        // Clear the graph
        cy.elements().remove();
  
        // Add the clicked node and its edges
        cy.add(neighborhood);
  
        // Fit the graph to the neighborhood
        applyCytoscapeStyles(cy);
        neighborhood.select();
     
      }
      else{
        // Use Cytoscape's breadthFirstSearch to get reachable elements
        const startNodeId = clickedNode.id();
        const bfsResult = cy.elements().breadthFirstSearch({
          roots: `#${startNodeId}`,
          directed: true
        });
    
        const reachableElements = bfsResult.path;
        //console.log('Reachable Elements:', reachableElements);
    
        // Clear the graph
        cy.elements().remove();
    
        // Add the reachable elements
        cy.add(reachableElements);
    
        // Apply the layout to the graph
        //cy.layout({ name: 'breadthfirst' }).run(); // or any other layout you prefer
        applyCytoscapeStyles(cy);
        // Select the reachable elements
        reachableElements.select();
      }
     // applyCytoscapeStyles(cy);
    }
 
  };

  const resetGraphState = () => {
    if (cyRef.current) {
      const cy = cyRef.current;
      cy.removeAllListeners();
      // Remove existing elements
      cy.elements().remove();
      // Add back initial elements
      cy.add(initTree);
      // Apply fresh layout and styles
      currentLayout.current = 'circle';
      applyCytoscapeStyles(cy);
      // Unselect all elements
      cy.elements().unselect();
      cy.on('click', 'node', handleNodeClick);
    }
  };

  const handleResetClick = () => {
    setSearchTerm(''); // Clear the search text
    resetGraphState();
  };


  const theme = createTheme({
    palette: {
      primary: {
        main: '#42a5f5',
      },
    },
  });

  return (
    
<ThemeProvider theme={theme}>
      <Container sx={{ bgcolor: "#f2f6fc" }} maxWidth={false} style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'auto', bgcolor: {theme}}}>
        {!isVerified ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Turnstile siteKey="0x4AAAAAAA5Lcw1iG38DvCLN" 
            onSuccess ={() => setIsVerified(true)}
            onExpire={() => setIsVerified(false)}
            onError={() => setIsVerified(false)}
             />
          </Box>
        ) : (
          <>
            <Box display="flex" flexDirection="column" alignItems="left" sx={{ mb: 2, mt: 2}}>
              <ToolbarSection
                logo={logo}
                searchTerm={searchTerm}
                handleDynamicSearch={handleDynamicSearch}
                handleResetClick={handleResetClick}
                handleCsvUpload={handleCsvUpload}
                fileUploaded={fileUploaded}
                switchtoggle={switchtoggle.current}
                handleCheckboxChange={handleCheckboxChange}
                csvData={csvData}
                isModalOpen={isModalOpen}
                setModalOpen={setModalOpen}
                handleColumnsSelect={handleColumnsSelect}
                setAboutOpen={setAboutOpen}
              />
              <AboutDialog aboutOpen={aboutOpen} setAboutOpen={setAboutOpen} />
            </Box>
            <Paper
              elevation={3}
              sx={{ bgcolor: "#f2f6f0" }} 
              style={{ height: '90vh', width: '100%', display: 'flex', flexDirection: 'column', marginBottom: '20px', overflow:'hidden' }}>
              { loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Box width="80%">
                    <LinearProgress value={"processing"}/>
                  </Box>
                </Box>
              ) : (!fileUploaded ? <MotionText/> : <CytoscapeComponent
                elements={trackTree}
                style={{ width: '100%', height: '100%'}} /* Use 100vh for full screen height */
                cy={(cy) => {
                  cyRef.current = cy;
                  cy.on('click', 'node', handleNodeClick);
                  applyCytoscapeStyles(cy); 
                }}
              /> )}
            </Paper>
          </>
        )}
      </Container>
    </ThemeProvider>
 
);
};

export default App;
